import { createReference, getReferenceData, getReferenceId } from '@quromedical/fhir-common';
import { CreateMediaData, Note } from '@quromedical/models';
import { handleDownload } from 'core/media';
import { NoteCard } from 'core/notes';
import { AssignmentResult } from 'core/task';
import { ItemComponent, Section, Skeleton, SkeletonProvider } from 'design-system';
import { logger } from 'helpers';
import { useTasksProps } from 'hooks/useTasks';
import { useUserSession } from 'hooks/useUserSession';
import { NotesApi, MediaApi } from 'integration/aggregate';
import React, { useCallback } from 'react';

const mediaApi = new MediaApi();
const notesApi = new NotesApi();

export const downloadAttachment = async (id: string) => {
  try {
    const media = await mediaApi.createDownloadUrl('', id);

    const url = media.url;

    if (url) {
      await handleDownload(url);
    }
  } catch (err) {
    logger.error(err);
  }
};

interface CommonProps {
  patientId: string;
  fromPins: boolean;
  canEdit: boolean;
  revalidate?: () => Promise<void>;
  useTaskProps: useTasksProps;
}

export type NoteItemData = Note & CommonProps;

const StatefulItem: React.FC<NoteItemData> = item => {
  const [pinned, setPinned] = React.useState(item.pinned);
  const session = useUserSession();
  const profile = session.getProfile();
  const canEdit = getReferenceId(item.assessor) === profile?.id;
  const { patientId } = item;
  const { create, addNote } = item.useTaskProps;

  const onPin = useCallback(async () => {
    try {
      logger.track({
        type: 'gamification',
        name: 'note-pin-press',
      });

      // for immediate user feedback
      setPinned(!pinned);
      const nextValue = !item.pinned;
      await notesApi.pinNote({
        id: item.id,
        media: [],
        pinned: nextValue,
        subjective: undefined,
      });
      item.revalidate?.();
    } catch (err) {
      logger.error(err);
    }
  }, [item, pinned]);

  const onEditSubmit = useCallback(
    async (subjective: string, media: CreateMediaData[]) => {
      try {
        await notesApi.updateNote({
          id: item.id,
          subjective,
          media,
          pinned: item.pinned,
        });
        await item.revalidate?.();
      } catch (error) {
        logger.error(error);
      }
    },
    [item]
  );

  const onTaskSubmit = useCallback(
    async (result?: AssignmentResult) => {
      if (!result) {
        return;
      }

      const referenceData = getReferenceData({ reference: result.performer });

      if (!referenceData) {
        return;
      }

      await create({
        patientId: patientId,
        code: result.code,
        description: result.note,
        focus: createReference('ClinicalImpression', item.id),
        performer: createReference(referenceData.resourceType, referenceData.id),
        requestedPeriodEnd: result.dueDate ? result.dueDate.toString() : undefined,
      });

      await item.revalidate?.();
    },
    [patientId, create, item]
  );

  return (
    <SkeletonProvider loading={item.useTaskProps.isLoading}>
      <Skeleton>
        <NoteCard
          patientId={patientId}
          note={{
            ...item,
            pinned,
          }}
          onAttachmentPress={downloadAttachment}
          canPin={item.canEdit}
          canEdit={canEdit}
          onPin={onPin}
          onEditSubmit={onEditSubmit}
          onTaskSubmit={onTaskSubmit}
          addNote={addNote}
        />
      </Skeleton>
    </SkeletonProvider>
  );
};

export const Item: ItemComponent<NoteItemData> = ({ item }) => (
  // note that for some reason using hooks in a component that's rendered by a list view causes
  // the render to crash, hence the separation of the stateful and stateless components
  <Section hasBottomMargin={false} hasTopMargin={false}>
    <StatefulItem {...item} />
  </Section>
);
